import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from "@/lib/utils";
import { Divider, Typography } from "@bookips/sds";
import { Fragment } from "react";
// 본문 텍스트 기본 스타일 (SDS Typography 로 대체 불가한 leading/tracking 조합)
const BASE_TEXT = "text-base leading-[1.5] tracking-[-0.16px]";
const LABEL_WIDTH_CLASS = {
    sm: "w-[100px]",
    md: "w-[140px]"
};
const SectionTitle = (param)=>/*#__PURE__*/ {
    let { children  } = param;
    return _jsx(Typography, {
        variant: "subtitle1",
        as: "p",
        className: "mb-3 text-gray-700",
        children: children
    });
};
/**
 * 요약 영역의 강조 표현.
 * strong 은 base 의 leading/tracking 을 유지해야 해서 Typography(자체 line-height) 대신 b 를 쓴다.
 * success / error 는 표 전용 톤이라 여기서는 기본 표기로 떨어진다.
 */ const InlineText = (param)=>{
    let { tone , children  } = param;
    if (tone === "strong") return /*#__PURE__*/ _jsx("b", {
        className: "text-blue-500",
        children: children
    });
    if (tone === "highlight") return /*#__PURE__*/ _jsx(Typography, {
        variant: "subtitle1",
        as: "span",
        className: "text-indigo-500",
        children: children
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
};
/**
 * 라벨과 값이 한 줄에 붙는 행 (요약 영역).
 * 라벨이 없으면 값만 렌더한다 ('사용범위 내 무제한').
 */ const InlineRow = (param)=>{
    let { row  } = param;
    var _row_tone;
    const tone = (_row_tone = row.tone) !== null && _row_tone !== void 0 ? _row_tone : "default";
    return(// indent: 상위 행에 딸린 부속 행 (구독 모드의 '이번 달 만료 예정될 횟수')
    /*#__PURE__*/ _jsxs("dl", {
        className: cn("mb-1 flex flex-wrap"),
        children: [
            row.label && /*#__PURE__*/ _jsx("dt", {
                className: cn(BASE_TEXT, "text-gray-500"),
                children: /*#__PURE__*/ _jsxs(InlineText, {
                    tone: tone,
                    children: [
                        row.indent && /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": "true",
                            children: "ㄴ "
                        }),
                        row.label,
                        " :",
                        " "
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("dd", {
                className: cn(BASE_TEXT, "text-gray-500", row.label && "ml-1"),
                children: [
                    /*#__PURE__*/ _jsx(InlineText, {
                        tone: tone,
                        children: row.value
                    }),
                    row.suffix
                ]
            })
        ]
    }));
};
/**
 * 라벨/값 행 (사용범위 표·구독 상세정보).
 * labelWidth 가 있으면 라벨 컬럼이 고정폭인 표, 없으면 '라벨 : 값'으로 붙는 컨텐츠 흐름이 된다.
 * 값 강조는 semibold + 상태 색.
 */ const TableRow = (param)=>{
    let { row , labelWidth  } = param;
    var _row_tone;
    const tone = (_row_tone = row.tone) !== null && _row_tone !== void 0 ? _row_tone : "default";
    const isEmphasized = tone !== "default";
    return(// 컨텐츠 흐름은 행 간격이 8px (시안). 마지막 행 아래 여백까지 유지해야 하므로 gap 이 아니라 mb 로 둔다
    /*#__PURE__*/ _jsxs("dl", {
        className: cn("flex flex-wrap", labelWidth ? "mb-1" : "mb-2"),
        children: [
            /*#__PURE__*/ _jsx("dt", {
                className: cn(BASE_TEXT, "text-gray-300", labelWidth && LABEL_WIDTH_CLASS[labelWidth]),
                children: /*#__PURE__*/ _jsxs(Typography, {
                    variant: "body1",
                    className: "text-secondary",
                    children: [
                        row.label,
                        !labelWidth && " : "
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("dd", {
                className: cn("flex flex-1", BASE_TEXT, "text-gray-700", !labelWidth && "ml-1"),
                children: /*#__PURE__*/ _jsxs(Typography, {
                    variant: "body1",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: isEmphasized ? "subtitle1" : "body1",
                            as: "span",
                            className: cn(tone === "default" && "text-primary", tone === "success" && "text-status-data", tone === "error" && "text-status-error"),
                            children: row.value
                        }),
                        row.suffix
                    ]
                })
            })
        ]
    }));
};
const SectionContent = (param)=>{
    let { section  } = param;
    if (section.layout === "inline") {
        return /*#__PURE__*/ _jsx("div", {
            children: section.rows.map((row, index)=>/*#__PURE__*/ _jsx(InlineRow, {
                    row: row
                }, "".concat(section.id, "-").concat(index)))
        });
    }
    if (section.layout === "table") {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx(SectionTitle, {
                    children: section.title
                }),
                section.rows.map((row, index)=>/*#__PURE__*/ _jsx(TableRow, {
                        row: row,
                        labelWidth: section.labelWidth
                    }, "".concat(section.id, "-").concat(index)))
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(SectionTitle, {
                children: section.title
            }),
            /*#__PURE__*/ _jsx("ul", {
                children: section.items.map((item, index)=>/*#__PURE__*/ _jsxs("li", {
                        className: cn(BASE_TEXT, "mb-1 whitespace-break-spaces text-gray-500 max-sm:text-sm"),
                        children: [
                            item,
                            "\xa0"
                        ]
                    }, "".concat(section.id, "-").concat(index)))
            })
        ]
    });
};
/**
 * 본문 렌더 전용. mode·ticket 을 모르고 layout 별로 행을 그리는 방법만 안다.
 * 분기는 전부 getPassInfoSections 에 있다.
 */ const PassInfoDialogBody = (param)=>/*#__PURE__*/ {
    let { sections  } = param;
    return _jsx("div", {
        className: "px-7 max-sm:px-5",
        children: sections.map((section, index)=>/*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    index > 0 && /*#__PURE__*/ _jsx(Divider, {
                        className: sections[index - 1].id === "summary" ? "my-5" : "my-7"
                    }),
                    /*#__PURE__*/ _jsx(SectionContent, {
                        section: section
                    })
                ]
            }, section.id))
    });
};
export default PassInfoDialogBody;
